// src/layouts/BaseLayout.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useTabs } from '../context/TabsContext';
import { trackPageVisit } from '../utils/sessionPersistence';
import Header from '../components/Header';
import MobileHeader from '../components/MobileHeader';
import Footer from '../components/Footer';
import MobileFooterNav from '../components/MobileFooterNav';
import TabBar from '../components/TabBar';
import DashboardSidebar from '../components/sidebar/DashboardSidebar';
import RightSidebar from '../components/sidebar/RightSidebar';
import OfflineIndicator from '../components/OfflineIndicator';
import IncomingCallToast from '../components/crm/IncomingCallToast';

const BaseLayout = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [headerEl, setHeaderEl] = useState(null);
    const location = useLocation();
    const {
        leftCollapsed,
        leftWidth,
        rightVisible,
        rightWidth,
        isMobile,
        applyLogisticsDefaults,
        applyRegularDefaults,
        savePreferences,
        getContentMargins
    } = useSidebar();
    const { isAuthenticated } = useContext(AuthContext);
    const { tabs } = useTabs();
    const tabBarHeight = isAuthenticated && tabs.length > 0 ? 40 : 0;
    // Desktop renders the dark <Footer> (~56px, py-4 + one text line) only when no tabs are open.
    const footerHeight = (!isMobile && isAuthenticated && tabs.length === 0) ? 56 : 0;

    useEffect(() => {
        const measureHeader = () => {
            const h = headerEl ? headerEl.getBoundingClientRect().height : 0;
            setHeaderHeight(prev => (Math.abs(prev - h) < 0.5 ? prev : h));
        };
        measureHeader();
        if (!headerEl) return;
        let frame = null;
        const observer = new ResizeObserver(() => {
            if (frame !== null) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                measureHeader();
            });
        });
        observer.observe(headerEl);
        return () => {
            if (frame !== null) cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [headerEl]);

    // Use ref to store savePreferences to avoid infinite loop
    const savePreferencesRef = useRef(savePreferences);
    useEffect(() => {
        savePreferencesRef.current = savePreferences;
    }, [savePreferences]);
    
    const handleSetSearchQuery = (query) => {
        console.log("BaseLayout handling search query:", query);
        setSearchQuery(query);
    };
    
    const handleToggleSettings = () => {
        setSettingsOpen(prev => !prev);
    };
    
    // Track page visits for persistence
    useEffect(() => {
        trackPageVisit(location.pathname);
    }, [location.pathname]);
    
    // Check if current route is a logistics page
    const isLogisticsPage = location.pathname.startsWith('/logistics');
    
    // Check if current route needs full height layout
    const isFullHeightPage = [
        '/logistics/route-planner',    // Route Planner
        '/logistics/tracking',         // Live Tracking Page
        '/logistics/routes',           // Route Management Page
        '/logistics/drivers',          // Driver Management
        '/logistics/vehicles',         // Vehicle Management
        '/logistics/locations',        // Location Management
        '/permission-control'          // Permission Control Page
    ].includes(location.pathname)
        || /^\/discounts\/(new|\d+)$/.test(location.pathname);  // Discount editor
    
    // Track if we've already applied defaults for this page type
    const [appliedDefaults, setAppliedDefaults] = useState({
        logistics: false,
        regular: false
    });
    
    // Apply page-specific sidebar defaults when route changes (only once per page type)
    useEffect(() => {
        const pageType = isLogisticsPage ? 'logistics' : 'regular';
        
        if (!appliedDefaults[pageType]) {
            if (isLogisticsPage) {
                applyLogisticsDefaults();
            } else {
                applyRegularDefaults();
            }
            setAppliedDefaults(prev => ({ ...prev, [pageType]: true }));
        }
    }, [location.pathname, isLogisticsPage, appliedDefaults, applyLogisticsDefaults, applyRegularDefaults]);
    
    // Save preferences when sidebar state changes (but debounce to avoid loops)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            savePreferencesRef.current(isLogisticsPage);
        }, 100); // Small delay to avoid save loops

        return () => clearTimeout(timeoutId);
    }, [leftCollapsed, rightVisible, leftWidth, rightWidth, isLogisticsPage]);
    
    // Calculate content margins using the getContentMargins method
    const margins = getContentMargins();
    const contentStyle = {
        marginLeft: margins.left,
        marginRight: margins.right,
        transition: 'margin 0.3s ease-in-out',
        paddingTop: isMobile ? '0' : `${headerHeight}px`,
        minHeight: `100vh`,
        ...(isFullHeightPage && !isMobile && {
            height: `calc(100vh - ${tabBarHeight + footerHeight}px)`,
            minHeight: `calc(100vh - ${tabBarHeight + footerHeight}px)`,
            maxHeight: `calc(100vh - ${tabBarHeight + footerHeight}px)`,
            overflow: 'hidden'
        }),
        ...(isFullHeightPage && isMobile && {
            height: `calc(100vh - ${headerHeight + 60}px)`,
            maxHeight: `calc(100vh - ${headerHeight + 60}px)`,
            overflow: 'auto'
        })
    };
    
    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {/* Header - Z-index higher than sidebars */}
            <div className="relative z-50 print:hidden">
                {isMobile
                    ? <MobileHeader
                        setSearchQuery={handleSetSearchQuery}
                        showSidebarToggle={true}
                        headerRef={setHeaderEl}
                      />
                    : <Header setSearchQuery={handleSetSearchQuery} headerRef={setHeaderEl} />
                }
            </div>
            
            {/* Main Content Container */}
            <div className="flex-1 relative min-h-0" style={{ paddingBottom: (isMobile ? 60 : 0) + tabBarHeight }}>
                {/* Dashboard Sidebar - Now under header */}
                <div className="print:hidden">
                <DashboardSidebar
                    settingsOpen={settingsOpen}
                    onToggleSettings={handleToggleSettings}
                    headerHeight={headerHeight}
                />
                </div>
                
                {/* Main content */}
                <main
                    className="min-h-full relative"
                    style={contentStyle}
                >
                    <Outlet context={{ searchQuery, setSearchQuery: handleSetSearchQuery, isMobile, headerHeight }} />
                </main>
                
                {/* Right Sidebar - Under header */}
                <div className="print:hidden">
                <RightSidebar headerHeight={headerHeight} />
                </div>
            </div>
            
            {/* Footer */}
            <div className="print:hidden">
            {/* Desktop only. The tab strip is a multi-window metaphor — on a
                phone you work one screen at a time and go back — and it sat
                directly above the footer nav, so the two together took ~110px
                off every counting screen. Tabs are still tracked in
                TabsContext, so they are all there when the same account opens
                the app on a desktop. */}
            {isAuthenticated && !isMobile && <TabBar />}
            {isMobile ? <MobileFooterNav /> : (isAuthenticated && tabs.length === 0 && <Footer />)}
            </div>
            <OfflineIndicator />
            {isAuthenticated && <IncomingCallToast />}
        </div>
    );
};

export default BaseLayout;